import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Phone, ArrowRight, Loader2, ArrowLeft, RefreshCw, CheckCircle2, Send, Sparkles } from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';
import axios from 'axios';

import { BACKEND_URL, API } from '../../utils/apiConfig';

// Get time-based greeting
const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good Morning";
  if (hour >= 12 && hour < 17) return "Good Afternoon";
  if (hour >= 17 && hour < 21) return "Good Evening";
  return "Hello";
};

export default function CustomerLoginPage() {
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingState, setSendingState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [step, setStep] = useState('mobile'); // 'mobile' | 'otp'
  const [otp, setOtp] = useState(['', '', '', '']);
  const [verificationId, setVerificationId] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [greeting, setGreeting] = useState('');
  
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // The `next` query param (set by CustomerProtectedRoute / event landing page)
  // is the source of truth for where to send the player after login — it's
  // deterministic, unlike relying purely on localStorage timing.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get('next');
    if (next) localStorage.setItem('post_login_redirect', next);
  }, []);

  // Fetch player name when mobile is entered (for greeting)
  const fetchPlayerName = useCallback(async (mobileNumber) => {
    try {
      // Try to get player info from contacts
      const response = await axios.get(`${API}/user/player-info/${mobileNumber}`);
      if (response.data?.name) {
        setPlayerName(response.data.name);
      }
    } catch (error) {
      // Player not found, that's okay
      setPlayerName('');
    }
  }, []);

  // Check for player name when mobile changes
  useEffect(() => {
    if (mobile.length === 10) {
      fetchPlayerName(mobile);
    } else {
      setPlayerName('');
    }
  }, [mobile, fetchPlayerName]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Auto-submit when all 4 digits are entered
  useEffect(() => {
    if (otp.every(digit => digit !== '') && step === 'otp' && !verifying) {
      handleVerifyOtp();
    }
  }, [otp, step, verifying]);

  // Auto-focus first OTP input when step changes to OTP
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    }
  }, [step]);

  const formatMobile = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 10);
  };

  const handleMobileChange = (e) => {
    setMobile(formatMobile(e.target.value));
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (digit && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length > 0) {
      const newOtp = ['', '', '', ''];
      for (let i = 0; i < pastedData.length && i < 4; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const nextEmptyIndex = newOtp.findIndex(d => d === '');
      if (nextEmptyIndex !== -1) {
        otpRefs[nextEmptyIndex].current?.focus();
      } else {
        otpRefs[3].current?.focus();
      }
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    
    if (mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setSendingState('sending');

    try {
      const response = await axios.post(`${API}/user/otp/send`, { mobile });
      
      // Show "OTP Sent" state briefly
      setSendingState('sent');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (response.data.otp_required) {
        setOtpRequired(true);
        setVerificationId(response.data.verification_id);
        setGreeting(getTimeGreeting());
        setStep('otp');
        setResendTimer(30);
        toast.success('OTP sent to your mobile');
      } else {
        // OTP not required, proceed with direct login
        await handleDirectLogin();
      }
    } catch (error) {
      console.error('Failed to send OTP:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
      setSendingState('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      toast.error('Please enter complete OTP');
      return;
    }

    setVerifying(true);

    try {
      const response = await axios.post(`${API}/user/otp/verify`, {
        mobile,
        otp: otpCode,
        verification_id: verificationId
      });

      if (response.data.success) {
        // Update player name from verification response
        if (response.data.player_name) {
          setPlayerName(response.data.player_name);
        }
        
        // Proceed with login (no greeting toast here)
        await handleDirectLogin();
      }
    } catch (error) {
      console.error('OTP verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleDirectLogin = async () => {
    setLoading(true);
    
    const result = await login(mobile);

    if (result.success) {
      // Simple welcome message without greeting
      toast.success(result.isNewUser ? 'Welcome to MAKS!' : 'Welcome back!');

      const qnext = new URLSearchParams(window.location.search).get('next');
      const redirect = qnext || localStorage.getItem('post_login_redirect');
      if (!result.isProfileComplete) {
        navigate(redirect ? `/profile-setup?next=${encodeURIComponent(redirect)}` : '/profile-setup');
      } else if (redirect) {
        localStorage.removeItem('post_login_redirect');
        navigate(redirect);
      } else {
        navigate('/');
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setOtp(['', '', '', '']);
    setSendingState('idle');
    await handleSendOtp();
  };

  const handleBack = () => {
    setStep('mobile');
    setOtp(['', '', '', '']);
    setVerificationId('');
    setSendingState('idle');
  };

  // Render loading state button content
  const renderButtonContent = () => {
    if (sendingState === 'sending') {
      return (
        <>
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Sending OTP...
        </>
      );
    }
    if (sendingState === 'sent') {
      return (
        <>
          <CheckCircle2 className="w-5 h-5 mr-2 text-white" />
          OTP Sent!
        </>
      );
    }
    if (loading) {
      return (
        <>
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Please wait...
        </>
      );
    }
    return (
      <>
        Continue
        <ArrowRight className="w-5 h-5 ml-2" />
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-4">
        <div className="w-full max-w-sm">
          {step === 'mobile' ? (
            <>
              {/* Hero Logo - dominant brand anchor */}
              <div className="flex justify-center" style={{ marginBottom: '1.5rem' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_d46453dd-0e5a-48c6-a128-ea68421e01c7/artifacts/eo8ftovm_maks%20logo%20white%20.png"
                  alt="MAKS Sports"
                  className="object-contain w-full"
                  style={{ height: '40vh', maxHeight: '320px', minHeight: '160px' }}
                />
              </div>
              <div className="text-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
                  Play Now
                </h1>
                <p className="text-slate-600 text-sm">
                  Ready to play? Enter your number to start playing
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
                <form onSubmit={handleSendOtp} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="mobile" className="text-slate-700 font-medium">
                      Mobile Number
                    </Label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-slate-500">
                        <Phone className="w-4 h-4" />
                        <span className="text-sm font-medium">+91</span>
                      </div>
                      <Input
                        id="mobile"
                        type="tel"
                        inputMode="numeric"
                        placeholder="9876543210"
                        value={mobile}
                        onChange={handleMobileChange}
                        required
                        maxLength={10}
                        className="pl-20 h-12 text-lg tracking-wider"
                        data-testid="mobile-input"
                        autoFocus
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      We'll create an account if you're new
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || mobile.length !== 10}
                    className={`w-full h-12 font-semibold rounded-xl shadow-lg transition-all duration-300 ${
                      sendingState === 'sent' 
                        ? 'bg-emerald-500 hover:bg-emerald-500' 
                        : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/25'
                    }`}
                    data-testid="continue-btn"
                  >
                    {renderButtonContent()}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                {/* Greeting with player name */}
                {playerName ? (
                  <div className="mb-4 animate-fade-in">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-100 to-teal-100 px-4 py-2 rounded-full mb-3">
                      <Sparkles className="w-4 h-4 text-emerald-600" />
                      <span className="text-emerald-700 font-medium text-sm">Welcome back!</span>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {greeting}, <span className="text-emerald-600">{playerName.split(' ')[0]}</span>! 👋
                    </h1>
                    <p className="text-slate-600 mt-2">
                      Enter the 4-digit code to continue
                    </p>
                  </div>
                ) : (
                  <>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                      Verify OTP
                    </h1>
                    <p className="text-slate-600">
                      Enter the 4-digit code sent to
                    </p>
                  </>
                )}
                <p className="text-emerald-600 font-semibold mt-1">
                  +91 {mobile}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
                <div className="space-y-6">
                  {/* OTP Input Boxes */}
                  <div className="flex justify-center gap-3" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onFocus={(e) => e.target.select()}
                        className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all duration-200 ${
                          digit 
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                            : 'border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                        }`}
                        data-testid={`otp-input-${index}`}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  {/* Verify Button */}
                  <Button
                    onClick={handleVerifyOtp}
                    disabled={verifying || otp.some(d => !d)}
                    className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
                    data-testid="verify-otp-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify & Continue
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Resend OTP */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleBack}
                      className="text-slate-600 hover:text-slate-800 text-sm font-medium flex items-center gap-1 transition-colors"
                      data-testid="back-btn"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Change Number
                    </button>
                    
                    <button
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || loading}
                      className={`text-sm font-medium flex items-center gap-1 transition-colors ${
                        resendTimer > 0 
                          ? 'text-slate-400 cursor-not-allowed' 
                          : 'text-emerald-600 hover:text-emerald-700'
                      }`}
                      data-testid="resend-otp-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Helpful tip */}
              <div className="mt-4 text-center">
                <p className="text-xs text-slate-500">
                  💡 Tip: You can paste the OTP directly from your SMS
                </p>
              </div>
            </>
          )}

          <p className="text-center text-xs text-slate-500 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy of MAKS SPORTS INFRA & TECH LLP
          </p>
        </div>
      </div>

      {/* Footer */}
      <CustomerFooter />

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
